/* assets/hero-slide-scheduler.js
   Schedules per-slide pre/post content & optional shutoff for the Hero Banner.
   - Interprets times in America/Denver by default (Salt Lake City)
   - Accepts: "YYYY-MM-DD HH:MM", "YYYY-MM-DDTHH:MM[:SS]", "M/D/YYYY h:mm [AM|PM]", or full ISO with offset/Z
   - Safely updates existing media (img/video/iframe) when possible
   - Re-applies on Theme Editor section reloads
*/

(function () {
  // -------------------- Timezone helpers (America/Denver) --------------------
  const SLC_TZ = 'America/Denver';

  function tzOffsetMinutesAt(dateUTC, timeZone) {
    const f = new Intl.DateTimeFormat('en-US', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false
    });
    const parts = f.formatToParts(dateUTC).reduce((o, p) => (o[p.type] = p.value, o), {});
    const y = +parts.year, m = +parts.month, d = +parts.day;
    const hh = +parts.hour, mm = +parts.minute, ss = +parts.second;
    const asIfLocal = Date.UTC(y, m - 1, d, hh, mm, ss);
    return (asIfLocal - dateUTC.getTime()) / 60000; // minutes
  }

  function getUTCForZonedLocal({ year, month, day, hour, minute, second }, timeZone) {
    const guess = new Date(Date.UTC(year, month - 1, day, hour || 0, minute || 0, second || 0));
    const off = tzOffsetMinutesAt(guess, timeZone);
    return new Date(guess.getTime() - off * 60000);
  }

  function parseUserTimeToDate(str) {
    if (!str || typeof str !== 'string') return null;
    const s = str.trim();
    if (/([zZ]|[+\-]\d{2}:\d{2})$/.test(s)) {
      const d = new Date(s);
      return isNaN(d) ? null : d;
    }
    const isoNoOffset = s.replace(' ', 'T');
    let m = isoNoOffset.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (m) {
      const [, y, mo, d, h, mi, sec] = m.map((x, i) => (i ? Number(x) : x));
      return getUTCForZonedLocal({ year: +y, month: +mo, day: +d, hour: +h, minute: +mi, second: +(sec || 0) }, SLC_TZ);
    }
    m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
    if (m) {
      let [, mo, d, y, h, mi, ap] = m;
      mo = +mo; d = +d; y = +y; h = +h; mi = +mi;
      if (ap) {
        const pm = ap.toUpperCase() === 'PM';
        if (h === 12) h = pm ? 12 : 0; else if (pm) h += 12;
      }
      return getUTCForZonedLocal({ year: y, month: mo, day: d, hour: h, minute: mi, second: 0 }, SLC_TZ);
    }
    return null;
  }

  // -------------------- State helpers --------------------
  function getSlideState(cfg, nowMs) {
    const launch = parseUserTimeToDate(cfg.launchAt);
    const shutoff = parseUserTimeToDate(cfg.shutoffAt);
    if (shutoff && nowMs >= shutoff.getTime()) return 'off';
    if (launch && nowMs < launch.getTime()) return 'pre';
    return 'post';
  }

  // -------------------- DOM helpers --------------------
  function isExternal(href) {
    if (!href) return false;
    try { return new URL(href, location.origin).origin !== location.origin; }
    catch { return false; }
  }

  function swapHTML(el, selector, html) {
    const target = el.querySelector(selector);
    if (!target) return;
    target.innerHTML = html || '';
  }

  function ensureButtonAnchor(wrapper, idx, defaultStyle) {
    const existing = wrapper.querySelector(`a.button:nth-of-type(${idx})`);
    if (existing) return existing;
    const a = document.createElement('a');
    a.className = `button ${defaultStyle || ''}`.trim();
    a.href = '#';
    wrapper.appendChild(a);
    return a;
  }

  function swapButton(el, idx, label, href, defaultStyle) {
    const wrap = el.querySelector('.hero__button');
    if (!wrap) return;
    const a = ensureButtonAnchor(wrap, idx, defaultStyle);
    if (!label) { a.style.display = 'none'; return; }
    a.style.display = '';
    a.textContent = label;
    if (href) a.setAttribute('href', href); else a.removeAttribute('href');
    if (isExternal(href)) { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer'); }
    else { a.removeAttribute('target'); a.removeAttribute('rel'); }
  }

  function updateCardLink(inner, href) {
    const link = inner.querySelector('.section-hero__link');
    if (!link) return;
    if (href) {
      link.setAttribute('href', href);
      if (isExternal(href)) { link.setAttribute('target', '_blank'); link.setAttribute('rel', 'noopener noreferrer'); }
      else { link.removeAttribute('target'); link.removeAttribute('rel'); }
    } else {
      link.removeAttribute('href');
    }
  }

  function selectMediaWrapper(inner, which) {
    if (which === 'mobile') return inner.querySelector('.hero__media.small-up-hide');
    return inner.querySelector('.hero__media.hero__media--desktop') ||
           inner.querySelector('.hero__media:not(.small-up-hide)');
  }

  function normalizeExternal(external) {
    if (!external) return null;
    if (typeof external === 'string') return external;
    if (external.url) return external.url;
    return null;
  }

  function normalizeVideoSources(videoSources) {
  if (!videoSources) return [];

  // If theme/Liquid gave us { sources: [...] }
  if (!Array.isArray(videoSources) && typeof videoSources === 'object' && Array.isArray(videoSources.sources)) {
    videoSources = videoSources.sources;
  }

  // If it's a single object, wrap it
  if (!Array.isArray(videoSources) && typeof videoSources === 'object' && (videoSources.url || videoSources.src)) {
    videoSources = [videoSources];
  }

  // If it's a single string URL, wrap it
  if (typeof videoSources === 'string') {
    videoSources = [videoSources];
  }

  if (!Array.isArray(videoSources)) return [];

  return videoSources
    .map(src => {
      if (typeof src === 'string') return { url: src, type: '' };
      return {
        url: src.url || src.src || '',
        type: src.mime_type || src.type || src.format || ''
      };
    })
    .filter(s => !!s.url);
}

  // SAFELY update the existing media element when possible
  function setMediaSmart(container, mediaCfg) {
    if (!container || !mediaCfg) return;

    const externalUrl = normalizeExternal(mediaCfg.external);
    const sources = normalizeVideoSources(mediaCfg.videoSources || []);
    const imageUrl = mediaCfg.image || null;

    const existingIframe = container.querySelector('iframe');
    const existingVideo  = container.querySelector('video');
    const existingImg    = container.querySelector('img');

    // External video (YouTube/Vimeo)
    if (externalUrl) {
      if (existingIframe) {
        if (existingIframe.src !== externalUrl) existingIframe.src = externalUrl;
      } else {
        container.innerHTML =
          `<div class="media media--16-9 hero__media-embed">
             <iframe src="${externalUrl}" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>
           </div>`;
      }
      return;
    }

    // Hosted video
if (sources.length) {
  const poster = mediaCfg.poster || mediaCfg.image || '';
  if (existingVideo) {
    existingVideo.innerHTML = sources.map(s => `<source src="${s.url}"${s.type ? ` type="${s.type}"` : ''}>`).join('');
    if (poster) existingVideo.setAttribute('poster', poster); else existingVideo.removeAttribute('poster');
    try { existingVideo.load(); } catch {}
    return;
  }
  const srcHTML = sources.map(s => `<source src="${s.url}"${s.type ? ` type="${s.type}"` : ''}>`).join('');
  container.innerHTML =
    `<video class="hero__video" playsinline muted autoplay loop preload="none"${poster ? ` poster="${poster}"` : ''}>
       ${srcHTML}
     </video>`;
  return;
}

    // Image
    if (imageUrl) {
      if (existingImg) {
        if (existingImg.getAttribute('src') !== imageUrl) {
          existingImg.setAttribute('src', imageUrl);
          existingImg.removeAttribute('srcset'); // prevent old responsive srcset overriding
        }
        return;
      }
      container.innerHTML = `<img src="${imageUrl}" alt="" loading="eager" decoding="async">`;
      return;
    }

    // Nothing provided: leave original content
  }

  // Apply pre/post pack to one slide
  function applyStateToSlide(innerEl, cfg, state) {
    const pack = state === 'pre' ? (cfg.pre || {}) : (cfg.post || {});

    // Headings
    if (pack.heading)    swapHTML(innerEl, '.hero__heading', pack.heading);
    if (pack.subheading) swapHTML(innerEl, '.hero__subheading', pack.subheading);

    // Buttons
    const b1 = pack.btn1 || {};
    const b2 = pack.btn2 || {};
    if (b1.label) swapButton(innerEl, 1, b1.label, b1.href, 'button--filled');
    if (b2.label) swapButton(innerEl, 2, b2.label, b2.href, 'button--outlined');
    if (b1.href)  updateCardLink(innerEl, b1.href);

    // Media (desktop + mobile)
    const desktopWrap = selectMediaWrapper(innerEl, 'desktop');
    const mobileWrap  = selectMediaWrapper(innerEl, 'mobile');

    if (desktopWrap) setMediaSmart(desktopWrap, pack.media || {});
    if (mobileWrap)  {
      const mm = Object.assign({}, pack.mediaMobile || {});
      if (!mm.image && pack.media && pack.media.image) mm.image = pack.media.image; // fallback
      setMediaSmart(mobileWrap, mm);
    }

    innerEl.dataset.heroSchedState = state;
  }

  // Hide or remove slide, keeping Swiper in sync
  function hideOrRemoveSlide(innerEl, remove) {
    const slide = innerEl.closest('.swiper-slide') || innerEl;
    const host = slide.closest('hero-slider');
    const swiper = host && host.slider ? host.slider : (slide.closest('.swiper')?.swiper || null);

    if (swiper && slide.classList.contains('swiper-slide')) {
      const idx = Array.from(swiper.slides || []).indexOf(slide);
      if (idx === -1) { slide.style.display = 'none'; return; }
      if (remove) {
        if (swiper.activeIndex === idx) {
          const next = Math.min(idx + 1, (swiper.slides?.length || 1) - 1);
          if (next !== idx) swiper.slideTo(next, 0);
        }
        swiper.removeSlide(idx);
        swiper.update();
      } else {
        slide.style.display = 'none';
        swiper.update();
        if (idx === swiper.activeIndex) {
          const to = Math.min(idx + 1, (swiper.slides?.length || 1) - 1);
          swiper.slideTo(to, 0);
        }
      }
    } else {
      slide.style.display = 'none';
    }
  }

  // Evaluate one / all slides
  function evaluateOne(inner) {
    const cfgEl = inner.querySelector('script.hero-slide-schedule');
    if (!cfgEl) return;
    let cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch { return; }

    const now = Date.now();
    const state = getSlideState(cfg, now);
    if (state === 'off') {
      hideOrRemoveSlide(inner, !!cfg.shutoffRemove);
      return;
    }
    if (inner.dataset.heroSchedState !== state) {
      applyStateToSlide(inner, cfg, state);
    } else {
      // state unchanged; still ensure media is present if it wasn't before
      const pack = state === 'pre' ? (cfg.pre || {}) : (cfg.post || {});
      const desktopWrap = selectMediaWrapper(inner, 'desktop');
      if (desktopWrap) setMediaSmart(desktopWrap, pack.media || {});
      const mobileWrap  = selectMediaWrapper(inner, 'mobile');
      if (mobileWrap)  {
        const mm = Object.assign({}, pack.mediaMobile || {});
        if (!mm.image && pack.media && pack.media.image) mm.image = pack.media.image;
        setMediaSmart(mobileWrap, mm);
      }
    }
  }

  function evaluateAllSlides() {
    document.querySelectorAll('.hero__inner').forEach(evaluateOne);
  }

  // Start scheduler
  function startScheduler() {
    evaluateAllSlides();
    setTimeout(evaluateAllSlides, 1000); // Swiper/init race cover
    const POLL_MS = 5000;
    setInterval(evaluateAllSlides, POLL_MS);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) evaluateAllSlides();
    });

    window.addEventListener('hero-slider:ready', evaluateAllSlides);

    // Theme Editor live reloads
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', evaluateAllSlides);
      document.addEventListener('shopify:section:unload', evaluateAllSlides);
      document.addEventListener('shopify:block:select', evaluateAllSlides);
      document.addEventListener('shopify:block:deselect', evaluateAllSlides);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startScheduler);
  } else {
    startScheduler();
  }

  // Debug helper
  window.__heroSchedulerDebug = {
    evaluateAllSlides,
    parseUserTimeToDate,
    getSlideState
  };
})();